import { FormEvent, ReactNode, useState } from "react";
import {
  addArrow,
  addArrowTag,
  deleteArrow,
  deleteSingleArrow,
  setArrowCredence,
  setArrowKind,
} from "@store/slices/workspaceSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import TextAreaModal from "@shared/components/TextAreaModal";
import Menu from "@mui/material/Menu";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Slider,
  Typography,
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import { closeArrowContextMenu } from "@features/annogram/canvas/state/canvasAuxSlice";
import { selectViewedRectangles } from "@store/selectors";
import type { ArrowKind } from "@/types/canvasTypes";

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

function nodeLabelFor(text: string | undefined, fallback: string): string {
  const t = (text ?? "").trim();
  return t ? truncate(t, 12) : fallback;
}

const ArrowContextMenu = () => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const rectangles = useAppSelector(selectViewedRectangles);
  const { arrowContextMenuIdx, coords } = useAppSelector(
    (state) => state.canvasAux
  );

  if (!arrowContextMenuIdx) return null;

  const [startRectIdx, endRectIdx] = arrowContextMenuIdx;

  const primaryArrow = rectangles[startRectIdx]?.arrows.find(
    (a) => a.endRectIdx === endRectIdx
  );
  const siblingArrow = rectangles[endRectIdx]?.arrows.find(
    (a) => a.endRectIdx === startRectIdx && !a.doNotRender
  );

  if (!primaryArrow) return null;

  const startLabel = nodeLabelFor(rectangles[startRectIdx]?.text, "start");
  const endLabel = nodeLabelFor(rectangles[endRectIdx]?.text, "end");
  const isPair = !!siblingArrow;
  const primaryKind = primaryArrow.kind;
  const siblingKind = siblingArrow?.kind;

  const closeMenu = () => {
    dispatch(closeArrowContextMenu());
    setModalOpen(false);
  };

  // ---- shared handlers ----

  const handleLabelSubmit = (e: FormEvent, textInput: string) => {
    e.preventDefault();
    dispatch(addArrowTag({ startRectIdx, endRectIdx, textInput }));
    closeMenu();
  };

  const handleCredenceChange = (_: Event, value: number | number[]) => {
    const credence = Array.isArray(value) ? (value[0] ?? 100) : value;
    dispatch(setArrowCredence({ startRectIdx, endRectIdx, credence }));
    // Do NOT close the menu — the user may want to keep dragging.
  };

  const handleDelete = () => {
    // deleteArrow already removes both directions, which matches what
    // "Delete" means in both single and pair mode.
    dispatch(deleteArrow({ startRectIdx, endRectIdx }));
    closeMenu();
  };

  // ---- single-edge handlers ----

  const handleSingleSetKind = (kind: ArrowKind) => {
    dispatch(setArrowKind({ startRectIdx, endRectIdx, kind }));
    closeMenu();
  };

  const handleSingleFlip = () => {
    // Direct toward the start node requires changing storage:
    // delete the current arrow and recreate it in the reverse direction.
    // Preserve tag and credence so the user doesn't lose annotations.
    dispatch(deleteSingleArrow({ startRectIdx, endRectIdx }));
    dispatch(
      addArrow({
        startRectIdx: endRectIdx,
        endRectIdx: startRectIdx,
        kind: "directed",
        tag: primaryArrow.tag,
        credence: primaryArrow.credence,
      })
    );
    closeMenu();
  };

  const handleSingleAddBothDirected = () => {
    // Promote the clicked edge to directed and add the reverse so the pair
    // depicts circularity. Equivalent to drag-A-onto-B then drag-B-onto-A
    // then pair-menu "Both directed", but reachable in one step.
    dispatch(setArrowKind({ startRectIdx, endRectIdx, kind: "directed" }));
    dispatch(
      addArrow({
        startRectIdx: endRectIdx,
        endRectIdx: startRectIdx,
        kind: "directed",
        tag: primaryArrow.tag,
        credence: primaryArrow.credence,
      })
    );
    closeMenu();
  };

  // ---- pair-edge handlers ----

  const handlePairBothKind = (kind: ArrowKind) => {
    dispatch(setArrowKind({ startRectIdx, endRectIdx, kind }));
    dispatch(
      setArrowKind({
        startRectIdx: endRectIdx,
        endRectIdx: startRectIdx,
        kind,
      })
    );
    closeMenu();
  };

  const handlePairKeepOnlyPrimary = () => {
    // Keep the clicked arrow (set to directed); delete the sibling.
    dispatch(
      deleteSingleArrow({
        startRectIdx: endRectIdx,
        endRectIdx: startRectIdx,
      })
    );
    dispatch(setArrowKind({ startRectIdx, endRectIdx, kind: "directed" }));
    closeMenu();
  };

  const handlePairKeepOnlySibling = () => {
    // Keep the sibling (set to directed); delete the clicked arrow.
    dispatch(deleteSingleArrow({ startRectIdx, endRectIdx }));
    dispatch(
      setArrowKind({
        startRectIdx: endRectIdx,
        endRectIdx: startRectIdx,
        kind: "directed",
      })
    );
    closeMenu();
  };

  const handlePairCollapseToBidirectional = () => {
    // Delete the sibling; mark the clicked arrow as bidirectional.
    dispatch(
      deleteSingleArrow({
        startRectIdx: endRectIdx,
        endRectIdx: startRectIdx,
      })
    );
    dispatch(setArrowKind({ startRectIdx, endRectIdx, kind: "bidirectional" }));
    closeMenu();
  };

  // ---- render helpers ----

  const checkSlot = (selected: boolean) => (
    <ListItemIcon sx={{ minWidth: 28 }}>
      {selected ? <Check fontSize="small" /> : null}
    </ListItemIcon>
  );

  let kindItems: ReactNode;
  if (!isPair) {
    kindItems = (
      <>
        <MenuItem onClick={() => handleSingleSetKind("undirected")}>
          {checkSlot(primaryKind === "undirected")}
          <ListItemText>Undirected</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleSingleSetKind("directed")}>
          {checkSlot(primaryKind === "directed")}
          <ListItemText>{`Directed → ${endLabel}`}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSingleFlip}>
          {checkSlot(false)}
          <ListItemText>{`Directed → ${startLabel}`}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSingleAddBothDirected}>
          {checkSlot(false)}
          <ListItemText>Both directed (circular)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleSingleSetKind("bidirectional")}>
          {checkSlot(primaryKind === "bidirectional")}
          <ListItemText>Bidirectional (↔)</ListItemText>
        </MenuItem>
      </>
    );
  } else {
    const bothUndirected =
      primaryKind === "undirected" && siblingKind === "undirected";
    const bothDirected =
      primaryKind === "directed" && siblingKind === "directed";

    kindItems = (
      <>
        <MenuItem onClick={() => handlePairBothKind("undirected")}>
          {checkSlot(bothUndirected)}
          <ListItemText>Both undirected</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePairBothKind("directed")}>
          {checkSlot(bothDirected)}
          <ListItemText>Both directed (circular)</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePairKeepOnlyPrimary}>
          {checkSlot(false)}
          <ListItemText>{`Keep only → ${endLabel}`}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePairKeepOnlySibling}>
          {checkSlot(false)}
          <ListItemText>{`Keep only → ${startLabel}`}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePairCollapseToBidirectional}>
          {checkSlot(false)}
          <ListItemText>Collapse to bidirectional (↔)</ListItemText>
        </MenuItem>
      </>
    );
  }

  return (
    <>
      <Menu
        open={true}
        anchorReference="anchorPosition"
        anchorPosition={{ left: coords.x, top: coords.y }}
        onClose={closeMenu}
      >
        {kindItems}
        <Divider />
        <MenuItem
          // Don't let slider events close the menu while dragging.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{ flexDirection: "column", alignItems: "stretch" }}
          disableRipple
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              width: "100%",
            }}
          >
            <Typography variant="body1">Credence</Typography>
            <Typography variant="body1" color="text.secondary">
              {primaryArrow.credence ?? 100}%
            </Typography>
          </Box>
          <Slider
            size="small"
            value={primaryArrow.credence ?? 100}
            onChange={handleCredenceChange}
            min={1}
            max={100}
            step={1}
            sx={{ width: 180, mt: -0.5, alignSelf: "center" }}
            aria-label="Credence"
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setModalOpen(true)}>
          {checkSlot(false)}
          <ListItemText>Label</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          {checkSlot(false)}
          <ListItemText>{isPair ? "Delete both" : "Delete"}</ListItemText>
        </MenuItem>
      </Menu>
      {isModalOpen ? (
        <TextAreaModal
          title="Edit Label:"
          initialInput={primaryArrow.tag}
          handleSubmit={handleLabelSubmit}
          handleCancel={closeMenu}
        />
      ) : null}
    </>
  );
};

export default ArrowContextMenu;
